<template>
    <div v-if="visible" class="app-info-card" @click.stop>
        <div class="app-info-content">
            <!-- Title Header -->
            <div class="app-info-header">
                <h4>{{ appData.title }}</h4>
            </div>

            <div class="app-info-features">
                <!-- Analysis Tool Type -->
                <div v-if="appData.analysisType" class="feature-item">
                    <img :src="infoIcon" class="feature-icon" alt="" />
                    <div class="feature-text">
                        <strong>What kind of analysis tool is this?</strong>
                        <p>{{ appData.analysisType }}</p>
                    </div>
                </div>

                <!-- Dataset Scope -->
                <div v-if="appData.datasetScope" class="feature-item">
                    <img :src="targetIcon" class="feature-icon" alt="" />
                    <div class="feature-text">
                        <strong>Scope of dataset</strong>
                        <p>{{ appData.datasetScope }}</p>
                    </div>
                </div>

                <!-- Granularity -->
                <div v-if="appData.granularity" class="feature-item">
                    <img :src="searchIcon" class="feature-icon" alt="" />
                    <div class="feature-text">
                        <strong>Granularity &amp; demo availability</strong>
                        <p>{{ appData.granularity }}</p>
                    </div>
                </div>
            </div>

            <!-- Bottom section with capability indicators - only shown if capabilities exist -->
            <div v-if="appData.capabilities && appData.capabilities.length > 0" class="capability-section">
                <div class="capability-categories">
                    <!-- Only show location category if there are active location capabilities -->
                    <div v-if="hasLocationCapabilities" class="capability-category">
                        <div class="capability-category-label">Locations</div>
                        <div class="capability-group-items">
                            <div v-for="cap in locationCapabilities" :key="cap.key" class="capability-item" :class="{ 'capability-active': isCapabilityActive(cap.key) }" :id="cap.key">
                                <div class="capability-icon-wrapper">
                                    <img :src="cap.icon" class="capability-icon" alt="" />
                                    <b-tooltip :target="cap.key" triggers="hover"> {{ cap.tooltipText }} </b-tooltip>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Only show screen category if there are active screen capabilities -->
                    <div v-if="hasScreenCapabilities" class="capability-category">
                        <div class="capability-category-label">Screens</div>
                        <div class="capability-group-items">
                            <div v-for="cap in screenCapabilities" :key="cap.key" class="capability-item" :class="{ 'capability-active': isCapabilityActive(cap.key) }" :id="cap.key">
                                <div class="capability-icon-wrapper" :class="{ 'capability-icon-devices': cap.key === 'devices' }">
                                    <img :src="cap.icon" class="capability-icon" :class="{ 'capability-icon-wide': cap.key === 'devices' }" alt="" />
                                    <b-tooltip :target="cap.key" triggers="hover"> {{ cap.tooltipText }} </b-tooltip>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
// Import all icon assets once at the component level for better caching
import infoIcon from "@/assets/images/info.svg"
import targetIcon from "@/assets/images/target.svg"
import searchIcon from "@/assets/images/looking-glass.svg"
import homeIcon from "@/assets/images/location-home.svg"
import awayIcon from "@/assets/images/location-away.svg"
import tvIcon from "@/assets/images/big-screen.svg"
import devicesIcon from "@/assets/images/devices.svg"

// Create the capabilities array at module level to avoid recreating it with each instance
const CAPABILITIES = [
    { key: "info", label: "Info", icon: infoIcon },
    { key: "target", label: "Target Audience", icon: targetIcon },
    { key: "search", label: "Search", icon: searchIcon },
    { key: "home", label: "In-Home", icon: homeIcon, group: "location", tooltipText: "In home" },
    { key: "away", label: "Away from Home", icon: awayIcon, group: "location", tooltipText: "Out of home" },
    { key: "tv", label: "TV Screens", icon: tvIcon, group: "screen", tooltipText: "TV screens" },
    { key: "devices", label: "Multiple Devices", icon: devicesIcon, group: "screen", tooltipText: "All screens" },
]

export default {
    name: "AppInfoCard",
    props: {
        visible: {
            type: Boolean,
            default: false,
        },
        appData: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            // Make imported icons available to the template
            infoIcon,
            targetIcon,
            searchIcon,
        }
    },
    computed: {
        // Use the module-level capabilities array
        capabilities() {
            return CAPABILITIES
        },
        locationCapabilities() {
            return this.capabilities.filter(cap => cap.group === "location")
        },
        screenCapabilities() {
            return this.capabilities.filter(cap => cap.group === "screen")
        },
        otherCapabilities() {
            return this.capabilities.filter(cap => !cap.group)
        },
        hasLocationCapabilities() {
            return this.locationCapabilities.some(cap => this.isCapabilityActive(cap.key))
        },
        hasScreenCapabilities() {
            return this.screenCapabilities.some(cap => this.isCapabilityActive(cap.key))
        },
    },
    methods: {
        isCapabilityActive(key) {
            if (!this.appData || !this.appData.capabilities) return false
            return this.appData.capabilities.includes(key)
        },
    },
}
</script>

<style scoped>
.app-info-card {
    user-select: text;
    cursor: default;
    left: 0px;
    top: 70px;
    position: absolute;
    z-index: 1000;
    width: 350px;
    background-color: white;
    border-radius: 8px;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.15);
    color: #333;
    overflow: hidden;
    animation: fadeIn 0.2s ease-in-out;
}
@keyframes fadeIn {
    from {
        opacity: 0;
    }
    to {
        opacity: 1;
    }
}

.app-info-header {
    padding: 20px 20px 5px 20px;
    border-bottom: 1px solid #eee;
}

.app-info-header h4 {
    margin: 0;
    color: #337ab7;
    font-weight: bold;
    font-size: 1.2rem;
    text-rendering: optimizeLegibility;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    letter-spacing: 0.01em;
}

.app-info-content {
    padding: 0;
    text-align: left;
}

.app-info-features {
    display: flex;
    flex-direction: column;
    gap: 15px;
    margin: 20px;
}

.feature-item {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 10px;
    border-radius: 6px;
    background-color: #f8f9fa;
    text-align: left;
}

.feature-icon {
    flex: 0 0 auto;
    width: 28px;
    height: 28px;
    filter: brightness(0.5);
    opacity: 1;
    transition: opacity 0.2s ease;
}

.feature-text {
    -webkit-user-drag: none; /* Disable drag in WebKit browsers */
    flex: 1;
    text-align: left;
    user-select: text; /* Allows text selection */
}

.feature-text strong {
    -webkit-user-drag: none; /* Disable drag in WebKit browsers */

    display: block;
    font-size: 0.9rem;
    margin-bottom: 4px;
    color: #333;
    text-align: left;
    user-select: text; /* Allows text selection */
}

.feature-text p {
    -webkit-user-drag: none; /* Disable drag in WebKit browsers */

    font-size: 0.8rem;
    margin: 0;
    color: #666;
    user-select: text; /* Allows text selection */
}

/* Capability indicators section styles */
.capability-section {
    display: flex;
    flex-direction: column;
    gap: 15px;
    margin-top: 30px;
    border-top: 1px solid #eee;
    padding-top: 20px;
    margin-bottom: 20px;
    margin-left: 20px;
    margin-right: 20px;
}

.capability-categories {
    display: flex;
    width: 100%;
}

.capability-category {
    display: flex;
    flex-direction: column;
    gap: 12px;
    flex: 1;
}

.capability-category-label {
    font-size: 0.85rem;
    color: #555;
    font-weight: 500;
    text-align: left;
    margin-bottom: -8px;
    margin-left: 8px;
    margin-right: 8px;
}

.capability-group-items {
    display: flex;
    justify-content: flex-start;
    gap: 10px;
    padding-left: 8px;
    padding-right: 8px;
}
.tooltip {
    pointer-events: none;
}
.capability-item {
    display: flex;
    flex-direction: column;
    align-items: center;
}

.capability-icon-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 52px;
    height: 52px;
    border-radius: 8px;
    background-color: #e0e0e0;
    margin-bottom: 6px;
    transition: background-color 0.2s ease;
}

.capability-active .capability-icon-wrapper {
    background-color: #888888;
}

.capability-icon {
    width: 30px;
    height: 30px;
    opacity: 0.6;
    transition: opacity 0.3s ease;
}

.capability-active .capability-icon {
    opacity: 1;
}

.capability-icon-devices {
    width: 85px;
    height: 52px;
}

.capability-icon-wide {
    width: 55px;
    height: 32px;
}
</style>
